import {
	Dialog,
	DialogPanel,
	Menu,
	MenuButton,
	MenuItem,
	MenuItems,
	Transition,
	TransitionChild,
} from '@headlessui/react';
import {
	ArrowRightStartOnRectangleIcon,
	Bars3Icon,
	ChartBarIcon,
	ChevronDownIcon,
	ClipboardDocumentListIcon,
	ClockIcon,
	Cog6ToothIcon,
	CurrencyDollarIcon,
	InboxArrowDownIcon,
	QueueListIcon,
	RectangleGroupIcon,
	StarIcon,
	UserCircleIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import cn from 'classnames';
import Logo from 'jsx:./img/tandem-logo-horizontal-white.svg';
import isUndefined from 'lodash.isundefined';
import PropTypes from 'prop-types';
import { Fragment, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import NotificationsContainer from './components/NotificationsContainer';
import { useUserStore } from './components/userStore';

const navigation = [
	{
		name: 'Incoming Referrals',
		href: '/incoming',
		icon: InboxArrowDownIcon,
		exAgencyOnly: true,
		countKey: 'incoming',
	},
	{
		name: 'My Worklist',
		href: '/worklist',
		icon: QueueListIcon,
		exAgencyOnly: false,
		countKey: 'worklist',
	},
	{
		name: 'My Worklist',
		href: '/worklist/incoming',
		icon: QueueListIcon,
		exAgencyOnly: true,
		countKey: 'worklist',
	},
	{
		name: 'In-Fulfillment',
		href: '/fulfillment',
		icon: ClipboardDocumentListIcon,
		exAgencyOnly: true,
		countKey: 'fulfillment',
	},
	{
		name: 'Outgoing Referrals',
		href: '/worklist/outgoing',
		icon: ArrowRightStartOnRectangleIcon,
		exAgencyOnly: true,
		countKey: 'outgoing',
	},
	{
		name: 'Wait List',
		href: '/wait-list',
		icon: ClockIcon,
		exAgencyOnly: true,
		countKey: 'waiting',
	},
];

function isRoute(locHash, itemHref) {
	if (itemHref.length > 1) {
		return locHash.startsWith(itemHref);
	}
	return locHash == itemHref;
}

const MenuLink = ({ path, name, Icon, badgeCount }) => {
	const locHash = location.hash.substring(1);
	return (
		<Link
			to={path}
			className={cn('text-white', { active: isRoute(locHash, path) })}
		>
			<div className="flex items-center flex-grow gap-2">
				<Icon className="w-6 h-6 shrink-0" aria-hidden="true" />
				{name}
			</div>
			{!isUndefined(badgeCount) && (
				<span className="badge badge-sm">{badgeCount}</span>
			)}
		</Link>
	);
};

MenuLink.propTypes = {
	path: PropTypes.string.isRequired,
	name: PropTypes.string.isRequired,
	Icon: PropTypes.elementType.isRequired,
	badgeCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const Layout = ({ hideNav }) => {
	const [sidebarOpen, setSidebarOpen] = useState(false);
	const { user } = useUserStore();
	useLocation();
	const menuCountsQuery = useQuery({
		queryKey: ['menu-counts', user?.coord_agency?.id],
		queryFn: async () => {
			const { data } = await axios.get(
				`/coord_agencies/${user?.coord_agency?.id}/menu-counts`,
			);
			return data;
		},
	});

	const isExecutingAgency = ['executing', 'healthplan', 'clinical'].includes(
		user?.coord_agency?.agency_type,
	);
	const isHealthplanAgency = ['healthplan', 'clinical'].includes(
		user?.coord_agency?.agency_type,
	);

	return (
		<>
			<Transition show={sidebarOpen} as={Fragment}>
				<Dialog className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
					<TransitionChild
						as={Fragment}
						enter="transition-opacity ease-linear duration-300"
						enterFrom="opacity-0"
						enterTo="opacity-100"
						leave="transition-opacity ease-linear duration-300"
						leaveFrom="opacity-100"
						leaveTo="opacity-0"
					>
						<div className="fixed inset-0 bg-gray-900/80" />
					</TransitionChild>

					<div className="fixed inset-0 flex">
						<TransitionChild
							as={Fragment}
							enter="transition ease-in-out duration-300 transform"
							enterFrom="-translate-x-full"
							enterTo="translate-x-0"
							leave="transition ease-in-out duration-300 transform"
							leaveFrom="translate-x-0"
							leaveTo="-translate-x-full"
						>
							<DialogPanel className="relative flex flex-1 w-full max-w-xs mr-16">
								<TransitionChild
									as={Fragment}
									enter="ease-in-out duration-300"
									enterFrom="opacity-0"
									enterTo="opacity-100"
									leave="ease-in-out duration-300"
									leaveFrom="opacity-100"
									leaveTo="opacity-0"
								>
									<div className="absolute top-0 flex justify-center w-16 pt-5 left-full">
										<button
											type="button"
											className="-m-2.5 p-2.5"
											onClick={() => setSidebarOpen(false)}
										>
											<span className="sr-only">Close sidebar</span>
											<XMarkIcon
												className="w-6 h-6 text-white"
												aria-hidden="true"
											/>
										</button>
									</div>
								</TransitionChild>
								{/* Sidebar component, swap this element with another sidebar if you like */}
								<div className="flex flex-col px-6 pb-4 overflow-y-auto grow gap-y-5 bg-tandem-600">
									<div className="flex items-center h-16 shrink-0">
										<Logo className="w-auto h-10" />
									</div>
									<nav className="flex flex-col flex-1">
										<ul role="list" className="flex flex-col flex-1 gap-y-7">
											<li>
												<ul role="list" className="-mx-2 space-y-1">
													{navigation
														.filter(
															({ exAgencyOnly }) =>
																exAgencyOnly === isExecutingAgency,
														)
														.map((item) => (
															<li key={item.name}>
																<MenuLink
																	path={item.href}
																	name={item.name}
																	Icon={item.icon}
																/>
															</li>
														))}
												</ul>
											</li>
											<li className="mt-auto">
												{isExecutingAgency && (
													<MenuLink
														path="/funds"
														name="Funds"
														Icon={CurrencyDollarIcon}
													/>
												)}
												<MenuLink
													path="/services"
													name="Services"
													Icon={RectangleGroupIcon}
												/>
												<MenuLink
													path="/reports"
													name="Reports"
													Icon={ChartBarIcon}
												/>
											</li>
										</ul>
									</nav>
								</div>
							</DialogPanel>
						</TransitionChild>
					</div>
				</Dialog>
			</Transition>

			{/* Static sidebar for desktop */}
			<div className={cn('app-nav', { hide: hideNav })}>
				{/* Sidebar component, swap this element with another sidebar if you like */}
				<div className="flex flex-col px-6 pb-4 overflow-y-auto grow gap-y-5 bg-tandem-600">
					<div className="flex items-center h-16 shrink-0">
						<Link to="/">
							<Logo className="w-auto h-10" />
						</Link>
					</div>
					{!hideNav && (
						<nav className="flex flex-col flex-1">
							<h3 className="text-white">{user?.coord_agency?.agency_name}</h3>
							<ul className="flex-grow px-0 -mx-4 menu">
								{navigation
									.filter(
										({ exAgencyOnly }) => exAgencyOnly === isExecutingAgency,
									)
									.map((item) => (
										<li key={item.name}>
											<MenuLink
												path={item.href}
												name={item.name}
												Icon={item.icon}
												badgeCount={
													menuCountsQuery.isFetched
														? menuCountsQuery.data[item.countKey]
														: '0'
												}
											/>
										</li>
									))}
							</ul>
							<ul className="px-0 -mx-4 menu">
								<li className="mt-auto">
									{isHealthplanAgency && (
										<MenuLink
											path="/admin-panel"
											name="Admin Panel"
											Icon={Cog6ToothIcon}
										/>
									)}
									{isExecutingAgency && !isHealthplanAgency && (
										<>
											<MenuLink
												path="/ilos-panel"
												name="Billing"
												Icon={StarIcon}
											/>
											<MenuLink
												path="/funds"
												name="Funds"
												Icon={CurrencyDollarIcon}
											/>
										</>
									)}
									<MenuLink
										path="/services"
										name="Services"
										Icon={RectangleGroupIcon}
									/>
									<MenuLink
										path="/reporting"
										name="Reporting"
										Icon={ChartBarIcon}
									/>
								</li>
							</ul>
						</nav>
					)}
				</div>
			</div>

			<div className="app-main">
				<div className="sticky top-0 z-40 flex items-center h-16 px-4 bg-white border-b border-gray-200 shadow-sm shrink-0 gap-x-4 sm:gap-x-6 sm:px-6 lg:px-8">
					<button
						type="button"
						className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
						onClick={() => setSidebarOpen(true)}
					>
						<span className="sr-only">Open sidebar</span>
						<Bars3Icon className="w-6 h-6" aria-hidden="true" />
					</button>

					{/* Separator */}
					<div
						className="w-px h-6 bg-gray-900/10 lg:hidden"
						aria-hidden="true"
					/>

					<div className="flex self-stretch flex-1 gap-x-4 lg:gap-x-6">
						{/* <form className="relative flex flex-1" action="#" method="GET">
							<label htmlFor="search-field" className="sr-only">
								Search
							</label>
							<MagnifyingGlassIcon
								className="absolute inset-y-0 left-0 w-5 h-full text-gray-400 pointer-events-none"
								aria-hidden="true"
							/>
							<input
								id="search-field"
								className="block w-full h-full py-0 pl-8 pr-0 text-gray-900 border-0 placeholder:text-gray-400 focus:ring-0 sm:text-sm"
								placeholder="Search..."
								type="search"
								name="search"
							/>
						</form> */}
						<div className="flex flex-1"></div>
						<div className="flex items-center gap-x-4 lg:gap-x-6">
							{/* <button
								type="button"
								className="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500"
							>
								<span className="sr-only">View notifications</span>
								<BellIcon className="w-6 h-6" aria-hidden="true" />
							</button> */}

							{/* Separator */}
							{/* <div
								className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-900/10"
								aria-hidden="true"
							/> */}

							{/* Profile dropdown */}
							<Menu as="div" className="relative">
								<MenuButton className="-m-1.5 flex items-center p-1.5">
									<span className="sr-only">Open user menu</span>
									<UserCircleIcon className="w-10 h-10 stroke-primary" />
									<span className="hidden lg:flex lg:items-center">
										<span
											className="ml-4 text-sm font-semibold leading-6 text-gray-900"
											aria-hidden="true"
										>
											{user?.name_first} {user?.name_last}
										</span>
										<ChevronDownIcon
											className="w-5 h-5 ml-2 text-gray-400"
											aria-hidden="true"
										/>
									</span>
								</MenuButton>
								<Transition
									as={Fragment}
									enter="transition ease-out duration-100"
									enterFrom="transform opacity-0 scale-95"
									enterTo="transform opacity-100 scale-100"
									leave="transition ease-in duration-75"
									leaveFrom="transform opacity-100 scale-100"
									leaveTo="transform opacity-0 scale-95"
								>
									<MenuItems className="absolute right-0 z-10 mt-2.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
										<MenuItem>
											<Link
												to="/preferences"
												className={cn(
													'block px-3 py-1 text-sm leading-6 text-gray-900',
												)}
											>
												Preferences
											</Link>
										</MenuItem>
										<MenuItem>
											{({ active }) => (
												<Link
													to="/logout"
													className={cn(
														{ 'bg-gray-50': active },
														'block px-3 py-1 text-sm leading-6 text-gray-900',
													)}
												>
													Sign out
												</Link>
											)}
										</MenuItem>
									</MenuItems>
								</Transition>
							</Menu>
						</div>
					</div>
				</div>

				<main className={cn({ 'ml-72': !hideNav })}>
					<Outlet />
				</main>
			</div>
			<NotificationsContainer />
		</>
	);
};

Layout.propTypes = {
	hideNav: PropTypes.bool,
};

export default Layout;
